import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { UserData } from "../../../../types/org.type";
import useGetRegtankOnboardingDetail from "../../../../hooks/useGetRegtankOnboardingDetail";

export interface RegtankPassportPhoto {
  userId?: number;
  requestId: string;
  frontDocumentPhoto: string;
  backDocumentPhoto: string;
  expiredDate: string;
}

interface UseRegtankPhotosInput {
  user: UserData[] | undefined;
  token: string;
  hasBeenSentToWallex: boolean;
}

interface CachedPhoto {
  userId?: number;
  requestId: string;
  frontDocumentPhoto: string;
  backDocumentPhoto: string;
  expiredDate: string;
  expiredAt: string;
}

// In-memory cache for regtank document photos (avoids storing PII in sessionStorage)
const regtankPhotoCache = new Map<string, CachedPhoto>();

export const useRegtankPhotos = ({
  user,
  token,
  hasBeenSentToWallex,
}: UseRegtankPhotosInput) => {
  const getRegtankOnboardingDetailMutation = useGetRegtankOnboardingDetail();
  const [regtankDocumentPhoto, setRegtankDocumentPhoto] = useState<
    RegtankPassportPhoto[]
  >([]);
  const [passportPhotoIsLoading, setPassportPhotoIsLoading] = useState(false);

  useEffect(() => {
    let requestIds: string[] = [];
    if (user && user.length > 0) {
      for (const data of user) {
        if (data.regtankRequest && data.regtankRequest?.length > 0)
          requestIds.push(data.regtankRequest?.[0].requestId);
      }
    }

    // Check in-memory cache for each requestId
    const cached: Record<string, CachedPhoto> = {};
    regtankPhotoCache.forEach((value, key) => {
      cached[key] = value;
    });

    const expired = requestIds.filter((x) => {
      const item = cached[x];
      if (!item) {
        return true;
      }
      return new Date(item.expiredAt) <= new Date();
    });
    setRegtankDocumentPhoto(Object.values(cached));
    if (expired.length > 0 && !hasBeenSentToWallex) {
      setPassportPhotoIsLoading(true);
      getRegtankOnboardingDetailMutation.mutate(
        { token, requestIds },
        {
          onSuccess: async (data) => {
            let regtankDocument: RegtankPassportPhoto[] = [];
            for (const detail of data) {
              const matchUser = user?.find(
                (x) =>
                  x.regtankRequest &&
                  x.regtankRequest.length > 0 &&
                  x.regtankRequest?.[0].requestId === detail.requestId
              );
              const photo: CachedPhoto = {
                userId: matchUser?.id,
                requestId: detail.requestId,
                frontDocumentPhoto: detail.frontDocumentPhoto,
                backDocumentPhoto: detail.backDocumentPhoto,
                expiredDate: detail.expiredAt,
                expiredAt: detail.expiredAt,
              };
              regtankDocument.push(photo);
              regtankPhotoCache.set(detail.requestId, photo);
            }
            setRegtankDocumentPhoto(regtankDocument);
            setPassportPhotoIsLoading(false);
          },
          onError: (e) => {
            setPassportPhotoIsLoading(false);
            toast.error((e as Error).message);
          },
        }
      );
    }
  }, [token, hasBeenSentToWallex, user]);

  return { regtankDocumentPhoto, passportPhotoIsLoading };
};
